import React, { useEffect, useState, useCallback } from 'react';
import { View } from 'react-native';
import { useRouter } from 'expo-router';
import ToolGrid from '@/components/Tools/ToolGrid';
import useResponsiveness from '@/hooks/useResponsiveness';
import { getExploreGridColumns } from '@/components/Explore/exploreGridColumns';
import { toolsApi } from '@/lib/api';

/**
 * Fetches and renders a grid of active tools that produce a given output type
 * (e.g. all `image` tools on the /images page, all `video` tools on /videos,
 * all `music` tools on /music). Self-contained — owns its own fetch state so
 * it can drop into any ExploreSection without polluting the global tool store
 * (which is already used for the /tools catalog page).
 *
 * Renders nothing while loading the first page if there are no cached tools
 * yet, and renders nothing if the fetch ultimately returns an empty list, so
 * the surrounding section header can be conditionally shown by the caller.
 */
const ToolsByOutputTypeSection = ({ outputType, limit = 12, onEmpty }) => {
  const router = useRouter();
  const responsive = useResponsiveness();
  const columns = getExploreGridColumns(responsive);
  const [tools, setTools] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    toolsApi
      .list({ outputType, limit })
      .then(data => {
        if (cancelled) return;
        const list = Array.isArray(data?.tools) ? data.tools : [];
        setTools(list);
        if (!list.length && typeof onEmpty === 'function') onEmpty();
      })
      .catch(() => {
        if (cancelled) return;
        setTools([]);
        if (typeof onEmpty === 'function') onEmpty();
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [outputType, limit, onEmpty]);

  const handleToolPress = useCallback(
    (tool) => {
      if (tool?.slug) router.push(`/tools/${tool.slug}`);
    },
    [router]
  );

  if (isLoading && !tools.length) {
    return (
      <View>
        <ToolGrid isLoading placeholderCount={Math.min(limit, 9)} columns={columns} />
      </View>
    );
  }

  if (!tools.length) return null;

  return <ToolGrid tools={tools.slice(0, 9)} onToolPress={handleToolPress} columns={columns} />;
};

export default React.memo(ToolsByOutputTypeSection);
